/**
 * ProductionRecordDetailView — VB6-style read-only viewer for an ICS production record.
 * Mirrors IcsTransferDetailView visually: header with labeled FieldBox rows, items table
 * with right-side Total Weight + expandable per-row weight panel, mobile card layout.
 *
 * Differences from the transfer view:
 *   - Header reflects production_summary fields (Operator / Production No. / Request Date /
 *     Finish Date / Production Location / Production Complete badge / Memo).
 *   - Items section is split into Input (p_type='I') / Output (p_type='O') tabs.
 *   - Total Weight reflects the active tab's rows.
 */
import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useQueries } from 'react-query';
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  Package,
  Printer,
} from 'lucide-react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { inventoryService } from '../../../services/inventoryService';
import {
  productionRecordService,
  type ProductionRecordDetail,
  type RecordDetailsResponse,
} from '../../../services/productionRecordService';
import { useEmployeeDisplayInfo } from '../../../hooks/useEmployeeDisplayInfo';
import { useAuth } from '../../../hooks/useAuth';
import { isManagerRole } from '../../../constants/queryConfig';
import api from '../../../services/api';
import toast from 'react-hot-toast';
import FieldBox from './FieldBox';

interface Props {
  /** Primary production record (the URL the user navigated to). */
  primary: RecordDetailsResponse;
  /** Sibling record when the URL is one half of an HTE -I/-O pair. */
  sibling?: RecordDetailsResponse | null;
  /**
   * Distinct linked records (WCO-2416/2418). For a multi-output production day one INPUT record
   * fans out to N OUTPUT records (one per destination); passing them individually lets the view
   * render one Output tab per destination instead of a single merged Output tab. Falls back to the
   * old primary+sibling behavior when not provided.
   */
  linkedRecords?: RecordDetailsResponse[];
  onBack?: () => void;
  /**
   * WCO-2677: which sub-tab to open on first render — 'I' (Input, the default) or 'O' (the first
   * Output/destination tab). When the view is embedded from the Output Detail tab, pass 'O' so the
   * user lands on the finished-goods output rather than the input material. Output tabs load async
   * (after the input), so 'O' is applied once the first Output tab appears; the user can still click
   * back to Input afterward.
   */
  initialTab?: 'I' | 'O';
  /**
   * WCO-2677: when true, the view is EMBEDDED (e.g. the Output Detail tab), not the standalone
   * `/production-records/:productionNumber` page. Embedded mode disables the address-bar sync effect —
   * on the standalone page that effect keeps the URL in step with the active tab, but from inside the
   * transfer-back view it would silently rewrite the browser URL to /production-records/<num>, breaking
   * refresh / back / deep-link expectations. (The transfer-side embed IcsTransferDetailView likewise
   * never touches the URL.)
   */
  embedded?: boolean;
}

// Tab id: 'I' = the Input tab; 'O:<production_number>' = one Output tab per destination record.
type TabKey = string;

const formatDate = (dateStr?: string | null) => {
  if (!dateStr || dateStr.length !== 8) return dateStr || '';
  return `${dateStr.slice(4, 6)}/${dateStr.slice(6, 8)}/${dateStr.slice(0, 4)}`;
};


const ProductionRecordDetailView = ({ primary, sibling, linkedRecords, onBack, initialTab = 'I', embedded = false }: Props) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isManager = isManagerRole(user?.role);  // Print Barcodes is manager-only
  // Runtime environment (dev/staging/local/cloud/production/edge). Print Barcodes is gated OFF on
  // production — shown on dev + staging (+ local). Same query key as Layout's banner (deduped/cached).
  const { data: envInfo } = useQuery<{ env: string; cloudEnv?: string }>(
    ['env-info'],
    async () => (await api.get('/env')).data,
    { staleTime: Infinity, refetchOnWindowFocus: false },
  );
  // Gate off `cloudEnv` (WCO-2762): it keys off NODE_ENV, so a DEPLOYED production container reads
  // 'production'. The legacy `env` field derives from DOPPLER_CONFIG, which is NOT forwarded into the
  // container (empty), so it returns 'cloud' in prod — the gate would fail OPEN and show Print Barcodes
  // on production. Prefer cloudEnv; fall back to env only for the (local) case where cloudEnv is absent.
  const resolvedEnv = envInfo?.cloudEnv || envInfo?.env;
  // Show unless we've confirmed we're on production (hide while env unknown → safe on prod).
  const barcodePrintAllowed = !!resolvedEnv && resolvedEnv !== 'production';
  const [activeTab, setActiveTab] = useState<TabKey>('I');
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const { getDisplayName, fetchEmployee } = useEmployeeDisplayInfo();
  const [, forceRender] = useState(0);

  // Pre-fetch operator names for every record so we don't flash the raw employee number
  // when the user switches tabs.
  useEffect(() => {
    const ids = new Set<string>();
    if (primary.summary.operator_id) ids.add(primary.summary.operator_id);
    if (sibling?.summary.operator_id) ids.add(sibling.summary.operator_id);
    for (const r of linkedRecords || []) {
      if (r.summary.operator_id) ids.add(r.summary.operator_id);
    }
    for (const id of ids) {
      if (!getDisplayName(id)) {
        fetchEmployee(id).then(() => forceRender(n => n + 1));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primary.summary.operator_id, sibling?.summary.operator_id,
      (linkedRecords || []).map(r => r.summary.operator_id).join(','),
      fetchEmployee, getDisplayName]);

  // Location code → description lookup (same source the transfer view uses)
  const { data: locationCodes } = useQuery(
    'location-codes',
    () => inventoryService.getLocationCodes(),
    { staleTime: 5 * 60 * 1000 },
  );
  const locationMap = useMemo(() => {
    if (!locationCodes) return new Map<string, string>();
    return new Map(locationCodes.map(l => [l.location_code, l.description]));
  }, [locationCodes]);
  const formatLocation = (code?: string | null) => {
    if (!code) return '';
    const desc = locationMap.get(code);
    return desc ? `${code} — ${desc}` : code;
  };

  // Build the tab set. One Input tab (all p_type='I' rows across every record), plus one Output tab
  // PER OUTPUT RECORD (destination) so a multi-output production day shows each destination
  // separately instead of a single merged Output tab (WCO-2416/2418). Each tab carries its own
  // rows, summary, and location. Falls back to the primary+sibling merge when linkedRecords isn't
  // provided (single-record / -I/-O suffix pairs).
  const { tabs, inputSummary } = useMemo(() => {
    // All records in play (deduped by production_number).
    const seen = new Set<string>();
    const allRecords: RecordDetailsResponse[] = [];
    for (const r of [primary, ...(linkedRecords || (sibling ? [sibling] : []))]) {
      const num = r.summary.production_number;
      if (num && seen.has(num)) continue;
      if (num) seen.add(num);
      allRecords.push(r);
    }

    const isDominantly = (rec: RecordDetailsResponse, target: 'I' | 'O') => {
      const i = rec.details.filter(d => d.p_type === 'I').length;
      const o = rec.details.filter(d => d.p_type === 'O').length;
      return target === 'I' ? i >= o : o > i;
    };

    // Input tab: all p_type='I' rows across every record; summary from the dominantly-input record.
    const inputRows = allRecords.flatMap(r => r.details.filter(d => d.p_type === 'I'));
    const inputRec = allRecords.find(r => isDominantly(r, 'I')) ?? primary;

    // Output tabs: one per record that has any output rows, ordered by production number
    // ascending so destinations appear in incremental order (e.g. 900521 · X before 900522 · M).
    // Numeric-aware compare: purely-numeric production numbers sort by value; anything else
    // (legacy -I/-O suffixes, non-numeric ICS numbers) falls back to a stable string compare.
    const prodNumKey = (n: string | null | undefined): number => {
      const v = Number(n);
      return Number.isFinite(v) ? v : Number.POSITIVE_INFINITY;
    };
    const outputRecs = allRecords
      .filter(r => r.details.some(d => d.p_type === 'O'))
      .sort((a, b) => {
        const ka = prodNumKey(a.summary.production_number);
        const kb = prodNumKey(b.summary.production_number);
        if (ka !== kb) return ka - kb;
        return (a.summary.production_number || '').localeCompare(b.summary.production_number || '');
      });

    type TabDef = { key: TabKey; kind: 'I' | 'O'; label: string; location?: string | null; rows: ProductionRecordDetail[]; summary: typeof primary.summary };
    const tabList: TabDef[] = [];
    tabList.push({ key: 'I', kind: 'I', label: t('productionRecords.inputTab') || 'Input', rows: inputRows, summary: inputRec.summary });
    for (const rec of outputRecs) {
      const num = rec.summary.production_number;
      // WCO-2459: the tab's location is the DESTINATION warehouse (e.g. X/M/B), not the facility. The
      // summary.location_code is deliberately the facility (HTE) per WCO-2427; the destination rides on each
      // output detail row (all O rows of an output share the same dest). Fall back to the summary if — defensively
      // — an output has no detail rows yet.
      const outputRows = rec.details.filter(d => d.p_type === 'O');
      const dest = outputRows.find(d => d.location_code)?.location_code || rec.summary.location_code;
      tabList.push({
        key: `O:${num}`,
        kind: 'O',
        label: dest || (t('productionRecords.outputTab') || 'Output'),
        location: dest,
        rows: outputRows,
        summary: rec.summary,
      });
    }

    return { tabs: tabList, inputSummary: inputRec.summary };
  }, [primary, sibling, linkedRecords, t]);

  // WCO-2677: when embedded from the Output Detail tab (initialTab='O'), land on the first Output tab
  // instead of Input. Output tabs load async (after the input record), so this is applied ONCE when the
  // first Output tab appears; a ref guards against re-selecting it if the user clicks back to Input.
  const appliedInitialOutputRef = useRef(false);

  // Keep the active tab valid when the tab set changes (e.g. outputs finish loading).
  useEffect(() => {
    if (!tabs.some(tb => tb.key === activeTab)) setActiveTab(tabs[0]?.key ?? 'I');

    if (initialTab === 'O' && !appliedInitialOutputRef.current) {
      const firstOutput = tabs.find(tb => tb.kind === 'O');
      if (firstOutput) {
        appliedInitialOutputRef.current = true;
        setActiveTab(firstOutput.key);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs.map(tb => tb.key).join('|'), initialTab]);

  const activeTabDef = tabs.find(tb => tb.key === activeTab) ?? tabs[0];
  const activeRows = activeTabDef?.rows ?? [];
  const activeSummary = activeTabDef?.summary ?? inputSummary;

  // Keep the address bar in sync with the active tab so URL, header, and tab always agree
  // (e.g. switching to the X output tab makes the URL /production-records/900521). WCO-2418.
  // Uses window.history.replaceState — NOT react-router navigate — on purpose: the page keys its
  // discovery/queries off the :productionNumber route param, and re-routing to an output number
  // would re-run discovery from that output (which can only see itself + its input), dropping the
  // sibling-destination tabs. replaceState updates the URL without notifying the router, so the
  // mounted page stays anchored to the record it loaded and every tab survives.
  // WCO-2677: skip URL sync entirely when embedded (Output Detail tab) — see the `embedded` prop docs.
  const activeProductionNumber = activeSummary?.production_number;
  useEffect(() => {
    if (embedded || !activeProductionNumber) return;
    const target = `/production-records/${encodeURIComponent(activeProductionNumber)}`;
    if (window.location.pathname !== target) {
      window.history.replaceState(window.history.state, '', target);
    }
  }, [activeProductionNumber, embedded]);

  // Weights per row come from the API directly (production_weight_info)
  const rowWeightSum = (d: ProductionRecordDetail): number => {
    if (d.weights && d.weights.length > 0) {
      return d.weights.reduce((sum, w) => sum + Number(w.weight), 0);
    }
    return Number(d.total_weight) || 0;
  };

  // Amount per VB6 ProductionRecord.frm rules:
  //   - Item Type N / W / P (weight-based)        →  rate × weight
  //   - Item Type F + priced_by_fixed_sub_unit='Y' → rate × qty_shipped × item_sub_quantity
  //   - everything else                            →  rate × qty_shipped
  const rowAmount = (d: ProductionRecordDetail): number => {
    const rate = Number(d.rate) || 0;
    const qtyShipped = Number(d.quantity_shipped) || 0;
    const itemType = d.item_type || '';
    if (itemType === 'N' || itemType === 'W' || itemType === 'P') {
      return rate * rowWeightSum(d);
    }
    if (itemType === 'F' && d.priced_by_fixed_sub_unit === 'Y') {
      const subQty = Number(d.item_sub_quantity) || 0;
      return rate * qtyShipped * subQty;
    }
    return rate * qtyShipped;
  };

  const totalWeight = useMemo(
    () => activeRows.reduce((sum, d) => sum + rowWeightSum(d), 0),
    [activeRows],
  );

  const expandedDetail = activeRows.find(d => d.id === expandedRowId) || null;

  const toggleRow = (id: number) => {
    setExpandedRowId(prev => (prev === id ? null : id));
  };

  // Reset expansion when the user switches tabs so the right-side weight panel doesn't
  // dangle on a row that's no longer visible.
  const switchTab = (tab: TabKey) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setExpandedRowId(null);
  };

  // Manager-only: open the printable barcode page for the active output. The endpoint is auth-
  // protected, so we fetch the HTML through the authenticated api client (Bearer token attached by
  // the axios interceptor) and render it as a blob — a raw window.open(url) navigation carries no token.
  // The tab must be opened SYNCHRONOUSLY in the click handler (pop-up blockers reject a window.open
  // that happens after an await), then navigated to the blob once the fetch resolves.
  const openBarcodes = async (outputNumber: string) => {
    const win = window.open('', '_blank');  // opened synchronously → not treated as a pop-up
    if (!win) { toast.error(t('productionRecords.printBarcodesPopupBlocked')); return; }
    try {
      // Path is relative to the api client's baseURL (/api) — no leading /api, matching
      // productionRecordService (api.get('/inventory/production-records/...')).
      const res = await api.get(
        `/inventory/production-records/${encodeURIComponent(outputNumber)}/barcodes`,
        { responseType: 'text' },
      );
      const blob = new Blob([res.data], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      win.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);  // revoke after the tab has loaded
    } catch {
      win.close();
      toast.error(t('productionRecords.printBarcodesFailed'));
    }
  };

  // Header shows the active tab's summary so navigating between Input/Output reveals
  // the linked record's metadata (production number, dates, memo).
  const summary = activeSummary;
  const isComplete = summary.is_approved === 'Y';
  const operatorName = getDisplayName(summary.operator_id) || summary.operator_id || '-';

  return (
    <div className="space-y-5 overflow-x-hidden">
      {/* Back row */}
      {onBack && (
        <div className="hidden md:flex items-center">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('productionRecords.backToList')}
          </button>
        </div>
      )}

      {/* Header section */}
      <div className="bg-white dark:bg-gray-800 md:rounded-lg md:shadow-sm md:border md:border-gray-200 md:dark:border-gray-700">
        {/* Title bar */}
        <div className="flex items-stretch gap-0 border-b border-gray-100 dark:border-gray-700">
          <div className="flex-1 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3 px-3 sm:px-5 py-2.5 sm:py-3">
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-semibold text-gray-800 dark:text-gray-200">
                  {t('productionRecords.productionNo')}
                </h2>
                <span className="font-mono text-base font-semibold text-blue-600 dark:text-blue-400 tracking-wide">
                  {summary.production_number}
                </span>
                {/* Manager-only: print the barcodes of the boxes scanned to THIS output.
                    Only on an Output tab (endpoint is output-scoped) and NOT on production. */}
                {isManager && barcodePrintAllowed && activeTabDef?.kind === 'O' && summary.production_number && (
                  <button
                    type="button"
                    onClick={() => openBarcodes(summary.production_number)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    title={t('productionRecords.printBarcodes')}
                  >
                    <Printer className="w-3.5 h-3.5" />
                    {t('productionRecords.printBarcodes')}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-5 text-sm text-gray-500 dark:text-gray-400">
                <span>
                  {t('productionRecords.operator')}:
                  <span className="ml-1 font-medium text-gray-700 dark:text-gray-300">{operatorName}</span>
                </span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                  isComplete
                    ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                    : 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800'
                }`}
              >
                {isComplete ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                {isComplete ? t('warehouseTransfer.completeCheck') : 'Pending'}
              </span>
            </div>
          </div>
        </div>

        {/* Mobile: compact card layout */}
        <div className="md:hidden px-3 py-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-[10px] uppercase text-gray-400 dark:text-gray-500">{t('productionRecords.requestDate')}</span>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{formatDate(summary.starting_date)}</p>
            </div>
            <div>
              <span className="text-[10px] uppercase text-gray-400 dark:text-gray-500">{t('productionRecords.finishDate')}</span>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{formatDate(summary.ending_date)}</p>
            </div>
            <div>
              <span className="text-[10px] uppercase text-gray-400 dark:text-gray-500">{t('productionRecords.productionLocation')}</span>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{formatLocation(summary.location_code) || '-'}</p>
            </div>
          </div>
          {summary.memo && (
            <div>
              <span className="text-[10px] uppercase text-gray-400 dark:text-gray-500">{t('productionRecords.memo')}</span>
              <p className="text-sm text-gray-700 dark:text-gray-300">{summary.memo}</p>
            </div>
          )}
        </div>

        {/* Desktop: form grid layout — mirrors the VB6 Scheduled Production Record form */}
        <div className="hidden md:block px-5 py-4">
          <div className="grid grid-cols-[140px_1fr_140px_1fr] items-center gap-x-3 gap-y-2.5">
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">{t('productionRecords.operator')}</span>
            <FieldBox value={operatorName} />
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">{t('productionRecords.productionLocation')}</span>
            <FieldBox value={formatLocation(summary.location_code)} />

            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">{t('productionRecords.requestDate')}</span>
            <FieldBox value={formatDate(summary.starting_date)} />
            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">{t('productionRecords.finishDate')}</span>
            <FieldBox value={formatDate(summary.ending_date)} />

            <span className="text-sm text-gray-500 dark:text-gray-400 text-right">{t('productionRecords.memo')}</span>
            <div className="col-span-3">
              <FieldBox value={summary.memo || ''} className="w-full block" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs strip — Input + one Output tab per destination (WCO-2416/2418) */}
      <div className="flex flex-wrap border-b border-gray-200 dark:border-gray-700">
        {tabs.map(tab => {
          const isActive = activeTab === tab.key;
          const label = tab.kind === 'I'
            ? (t('productionRecords.inputTab') || 'Input')
            : `${t('productionRecords.outputTab') || 'Output'}${tab.location ? ` · ${formatLocation(tab.location)}` : ''}`;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => switchTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                isActive
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {label}
              <span className="ml-2 text-xs text-gray-400">({tab.rows.length})</span>
            </button>
          );
        })}
      </div>

      {/* Items + total weight */}
      <div className="space-y-4 md:space-y-0 min-w-0">
        <div className="flex flex-col md:flex-row gap-4 items-start min-w-0">
          {/* Items table */}
          <div className="w-full md:flex-1 min-w-0 md:bg-white md:dark:bg-gray-800 md:rounded-lg md:shadow-sm md:border md:border-gray-200 md:dark:border-gray-700 overflow-hidden">
            <div className="bg-gray-100 dark:bg-gray-700 px-3 sm:px-5 py-2 sm:py-2.5 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                {activeTabDef?.kind === 'I'
                  ? (t('productionRecords.inputTab') || 'Input')
                  : `${t('productionRecords.outputTab') || 'Output'}${activeTabDef?.location ? ` · ${formatLocation(activeTabDef.location)}` : ''}`}
              </h3>
            </div>
            {activeRows.length === 0 ? (
              <div className="p-10 text-center text-gray-400 dark:text-gray-500">
                <Package className="mx-auto w-10 h-10 mb-2 opacity-50" />
                <p className="text-sm">{t('warehouseTransfer.noItems')}</p>
              </div>
            ) : (
              <>
                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700">
                  {activeRows.map((d, idx) => {
                    const hasWeights = d.weights && d.weights.length > 0;
                    const isExpanded = expandedRowId === d.id;
                    return (
                      <div
                        key={d.id}
                        className={`px-3 py-2.5 ${hasWeights ? 'cursor-pointer active:bg-gray-50 dark:active:bg-gray-700' : ''}`}
                        onClick={() => hasWeights && toggleRow(d.id)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-400 font-medium">{idx + 1}.</span>
                              <span className="font-mono text-sm font-medium text-gray-900 dark:text-white">{d.item_number}</span>
                              {d.is_frozen === 'Y' && <span className="text-[10px] text-blue-500">*</span>}
                              {hasWeights && (
                                isExpanded
                                  ? <ChevronDown className="w-3 h-3 text-blue-500" />
                                  : <ChevronRight className="w-3 h-3 text-gray-400" />
                              )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                              {d.item_chinese_name && <span>{d.item_chinese_name} </span>}
                              {d.item_english_name || '-'}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-lg font-bold text-gray-900 dark:text-white">
                              {Number(d.quantity || 0).toFixed(0)}{' '}
                              <span className="text-sm font-medium text-gray-500">{d.unit || ''}</span>
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{rowWeightSum(d).toFixed(2)} lbs</p>
                          </div>
                        </div>
                        {isExpanded && hasWeights && (
                          <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                            <div className="grid grid-cols-4 gap-1">
                              {d.weights.map((w, wi) => (
                                <div key={wi} className="text-center py-1 bg-gray-50 dark:bg-gray-700/50 rounded text-xs font-mono text-gray-700 dark:text-gray-300">
                                  {Number(w.weight).toFixed(2)}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Desktop table — column order matches the VB6 ProductionRecord form.
                    The Input and Output tabs use different column sets to mirror the
                    VB6 split between input materials and output finished goods. */}
                <div className="hidden md:block overflow-x-auto scrollbar-visible">
                  {activeTabDef?.kind === 'I' ? (
                    <table className="w-max min-w-full">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                          <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-12">#</th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap">
                            {t('warehouseTransfer.itemNumber')}
                          </th>
                          <th className="px-3 py-2.5 w-8" aria-label="frozen indicator" />
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-56 whitespace-nowrap">
                            {t('warehouseTransfer.itemName')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap">
                            {t('productionRecords.locationCol')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap">
                            {t('productionRecords.warehouseCol')}
                          </th>
                          <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16">
                            {t('warehouseTransfer.unit')}
                          </th>
                          <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24 whitespace-nowrap">
                            {t('productionRecords.quantity')}
                          </th>
                          <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24 whitespace-nowrap">
                            {t('productionRecords.quantityShipped')}
                          </th>
                          <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap">
                            {t('productionRecords.unitPrice')}
                          </th>
                          <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap">
                            {t('productionRecords.amount')}
                          </th>
                          <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap">
                            {t('productionRecords.weightShipped')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap">
                            {t('warehouseTransfer.dateCode')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32 whitespace-nowrap">
                            {t('warehouseTransfer.remark')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32 whitespace-nowrap">
                            {t('productionRecords.materialDocNo')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32 whitespace-nowrap">
                            {t('productionRecords.materialDocType')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeRows.map((d, idx) => {
                          const hasWeights = d.weights && d.weights.length > 0;
                          const isExpanded = expandedRowId === d.id;
                          return (
                            <tr
                              key={d.id}
                              className={`
                                border-b border-gray-100 dark:border-gray-700/50
                                ${hasWeights ? 'cursor-pointer' : ''}
                                ${isExpanded ? 'bg-blue-50 dark:bg-blue-900/20' : idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/50 dark:bg-gray-800/50'}
                                hover:bg-blue-50/70 dark:hover:bg-blue-900/20 transition-colors
                              `}
                              onClick={() => hasWeights && toggleRow(d.id)}
                            >
                              <td className="px-3 py-2.5 text-center text-sm text-gray-400 dark:text-gray-500 w-12">
                                <div className="flex items-center justify-center gap-1">
                                  <span>{idx + 1}</span>
                                  {hasWeights && (
                                    isExpanded
                                      ? <ChevronDown className="w-3.5 h-3.5 text-blue-500" />
                                      : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2.5 font-mono text-sm text-gray-900 dark:text-gray-100 w-28 whitespace-nowrap">
                                {d.item_number}
                              </td>
                              <td className="px-1 py-2.5 text-center text-sm text-gray-500 dark:text-gray-400 w-8">
                                {d.is_frozen === 'Y' ? '*' : '+'}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 w-56 whitespace-nowrap">
                                {d.item_chinese_name && (
                                  <span className="text-gray-500 dark:text-gray-400">{d.item_chinese_name} </span>
                                )}
                                {d.item_english_name || '-'}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-28 whitespace-nowrap">
                                {d.location_code || '-'}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-28 whitespace-nowrap">
                                {d.warehouse_code || '-'}
                              </td>
                              <td className="px-3 py-2.5 text-center text-sm text-gray-600 dark:text-gray-300 w-16">
                                {d.unit || ''}
                              </td>
                              <td className="px-3 py-2.5 text-right font-mono text-sm text-gray-900 dark:text-gray-100 w-24 whitespace-nowrap">
                                {d.quantity != null ? Number(d.quantity).toFixed(2) : ''}
                              </td>
                              <td className="px-3 py-2.5 text-right font-mono text-sm text-gray-900 dark:text-gray-100 w-24 whitespace-nowrap">
                                {d.quantity_shipped != null ? Number(d.quantity_shipped).toFixed(2) : ''}
                              </td>
                              <td className="px-3 py-2.5 text-right font-mono text-sm text-gray-900 dark:text-gray-100 w-28 whitespace-nowrap">
                                {Number(d.rate || 0).toFixed(2)}
                              </td>
                              <td className="px-3 py-2.5 text-right font-mono text-sm text-gray-900 dark:text-gray-100 w-28 whitespace-nowrap">
                                {rowAmount(d).toFixed(2)}
                              </td>
                              <td className="px-3 py-2.5 text-right font-mono text-sm text-gray-900 dark:text-gray-100 w-28 whitespace-nowrap">
                                {rowWeightSum(d).toFixed(2)}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-28 whitespace-nowrap">
                                {d.date_code || '-'}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-32 whitespace-nowrap">
                                {d.remark || '-'}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-32 whitespace-nowrap">
                                {d.material_doc_no || '-'}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-32 whitespace-nowrap">
                                {d.material_doc_type || '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <table className="w-max min-w-full">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                          <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-12">#</th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap">
                            {t('warehouseTransfer.itemNumber')}
                          </th>
                          <th className="px-3 py-2.5 w-8" aria-label="frozen indicator" />
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-56 whitespace-nowrap">
                            {t('warehouseTransfer.itemName')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap">
                            {t('productionRecords.locationCol')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap">
                            {t('productionRecords.warehouseCol')}
                          </th>
                          <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16">
                            {t('warehouseTransfer.unit')}
                          </th>
                          <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24 whitespace-nowrap">
                            {t('productionRecords.quantityShipped')}
                          </th>
                          <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap">
                            {t('productionRecords.unitPrice')}
                          </th>
                          <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap">
                            {t('productionRecords.amount')}
                          </th>
                          <th className="px-3 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap">
                            {t('productionRecords.weightShipped')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32 whitespace-nowrap">
                            {t('productionRecords.referenceNo')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap">
                            {t('warehouseTransfer.dateCode')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32 whitespace-nowrap">
                            {t('warehouseTransfer.remark')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24 whitespace-nowrap">
                            {t('warehouseTransfer.qsType')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24 whitespace-nowrap">
                            {t('warehouseTransfer.duration')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap">
                            {t('warehouseTransfer.scanComplete')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24 whitespace-nowrap">
                            {t('warehouseTransfer.qsPend')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 whitespace-nowrap">
                            {t('warehouseTransfer.qsRecNumber')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24 whitespace-nowrap">
                            {t('productionRecords.eItem')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32 whitespace-nowrap">
                            {t('warehouseTransfer.salespersonQs1')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeRows.map((d, idx) => {
                          const hasWeights = d.weights && d.weights.length > 0;
                          const isExpanded = expandedRowId === d.id;
                          return (
                            <tr
                              key={d.id}
                              className={`
                                border-b border-gray-100 dark:border-gray-700/50
                                ${hasWeights ? 'cursor-pointer' : ''}
                                ${isExpanded ? 'bg-blue-50 dark:bg-blue-900/20' : idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/50 dark:bg-gray-800/50'}
                                hover:bg-blue-50/70 dark:hover:bg-blue-900/20 transition-colors
                              `}
                              onClick={() => hasWeights && toggleRow(d.id)}
                            >
                              <td className="px-3 py-2.5 text-center text-sm text-gray-400 dark:text-gray-500 w-12">
                                <div className="flex items-center justify-center gap-1">
                                  <span>{idx + 1}</span>
                                  {hasWeights && (
                                    isExpanded
                                      ? <ChevronDown className="w-3.5 h-3.5 text-blue-500" />
                                      : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2.5 font-mono text-sm text-gray-900 dark:text-gray-100 w-28 whitespace-nowrap">
                                {d.item_number}
                              </td>
                              <td className="px-1 py-2.5 text-center text-sm text-gray-500 dark:text-gray-400 w-8">
                                {d.is_frozen === 'Y' ? '*' : '+'}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 w-56 whitespace-nowrap">
                                {d.item_chinese_name && (
                                  <span className="text-gray-500 dark:text-gray-400">{d.item_chinese_name} </span>
                                )}
                                {d.item_english_name || '-'}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-28 whitespace-nowrap">
                                {d.location_code || '-'}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-28 whitespace-nowrap">
                                {d.warehouse_code || '-'}
                              </td>
                              <td className="px-3 py-2.5 text-center text-sm text-gray-600 dark:text-gray-300 w-16">
                                {d.unit || ''}
                              </td>
                              <td className="px-3 py-2.5 text-right font-mono text-sm text-gray-900 dark:text-gray-100 w-24 whitespace-nowrap">
                                {d.quantity_shipped != null ? Number(d.quantity_shipped).toFixed(2) : ''}
                              </td>
                              <td className="px-3 py-2.5 text-right font-mono text-sm text-gray-900 dark:text-gray-100 w-28 whitespace-nowrap">
                                {Number(d.rate || 0).toFixed(2)}
                              </td>
                              <td className="px-3 py-2.5 text-right font-mono text-sm text-gray-900 dark:text-gray-100 w-28 whitespace-nowrap">
                                {rowAmount(d).toFixed(2)}
                              </td>
                              <td className="px-3 py-2.5 text-right font-mono text-sm text-gray-900 dark:text-gray-100 w-28 whitespace-nowrap">
                                {rowWeightSum(d).toFixed(2)}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-32 whitespace-nowrap">
                                {d.reference_number || '-'}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-28 whitespace-nowrap">
                                {d.date_code || '-'}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-32 whitespace-nowrap">
                                {d.remark || '-'}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-24 whitespace-nowrap">
                                {d.qs_type || '-'}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-24 whitespace-nowrap">
                                {d.duration != null ? String(d.duration) : '-'}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-28 whitespace-nowrap">
                                {d.receiving_scan_complete || '-'}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-24 whitespace-nowrap">
                                {d.pending_for_qs_allocation || '-'}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-28 whitespace-nowrap">
                                {d.qs_allocation_record_number || '-'}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-24 whitespace-nowrap">
                                {d.e_item || '-'}
                              </td>
                              <td className="px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 w-32 whitespace-nowrap">
                                {d.salesperson_number_qs1 || '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Right column — Total Weight + weight detail (desktop only) */}
          <div className="hidden md:block shrink-0 w-44 space-y-3">
            <div className="border border-gray-200 dark:border-gray-600 rounded-lg px-4 py-3 bg-white dark:bg-gray-800 shadow-sm border-l-4 border-l-green-500 dark:border-l-green-600">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                {t('warehouseTransfer.totalWeightLabel')}
              </div>
              <div className="text-2xl font-bold font-mono text-green-600 dark:text-green-400 text-right tabular-nums">
                {totalWeight.toFixed(2)}
              </div>
            </div>

            {expandedDetail && expandedDetail.weights && expandedDetail.weights.length > 0 && (
              <div className="border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
                <div className="bg-gray-100 dark:bg-gray-700 px-3 py-2 border-b border-gray-200 dark:border-gray-600">
                  <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 font-mono">
                    {expandedDetail.item_number}
                  </div>
                </div>
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-900/50">
                      <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 w-10">#</th>
                      <th className="px-3 py-1.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400">
                        {t('warehouseTransfer.weightLb')}
                      </th>
                    </tr>
                  </thead>
                </table>
                <div className="max-h-[192px] overflow-y-auto">
                  <table className="min-w-full">
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-600">
                      {expandedDetail.weights.map((w, wIdx) => (
                        <tr key={w.id} className="bg-white dark:bg-gray-800">
                          <td className="px-3 py-1 text-xs text-gray-500 dark:text-gray-400 w-10 select-none">{wIdx + 1}</td>
                          <td className="px-3 py-1 text-xs text-right font-mono text-gray-900 dark:text-gray-100">
                            {Number(w.weight).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 px-3 py-1.5 flex justify-between">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Total</span>
                  <span className="text-xs font-medium font-mono text-gray-900 dark:text-gray-100">
                    {expandedDetail.weights.reduce((sum, w) => sum + Number(w.weight), 0).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile: total weight banner */}
        <div className="w-full md:hidden border border-gray-200 dark:border-gray-600 rounded-lg px-4 py-3 bg-white dark:bg-gray-800 shadow-sm border-l-4 border-l-green-500 dark:border-l-green-600">
          <div className="flex items-center justify-between">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('warehouseTransfer.totalWeightLabel')}
            </div>
            <div className="text-2xl font-bold font-mono text-green-600 dark:text-green-400 tabular-nums">
              {totalWeight.toFixed(2)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Page wrapper — reads :productionNumber from the URL, fetches details, and discovers
 * any linked record so both tabs can be populated from one navigation. Production
 * records pair up in two ways:
 *   1. -I / -O suffix:  "900085-I" ↔ "900085-O"
 *   2. reference_numbers: input "900171" is referenced by output "900172" (different
 *      base numbers, linked only via production_detail.reference_number).
 *
 * Discovery flow:
 *   - Always fetch the primary record's details.
 *   - Fetch the records-list endpoint searching for the production number; the matching
 *     row's `reference_numbers` (when this record is the Output) and any row whose
 *     `reference_numbers` contains us (when this record is the Input) point at the
 *     linked record. Fetch its details too.
 *   - As a fallback for the suffix case, also try ±suffix swap.
 *   - Merge all fetched halves; the detail view splits them by p_type into Input/Output.
 */
export const ProductionRecordDetailPage = ({
  productionNumber,
  onBack,
  initialTab,
  embedded,
}: {
  productionNumber: string;
  onBack?: () => void;
  /** WCO-2677: 'O' opens the first Output sub-tab (used by the Output Detail embed); default is Input. */
  initialTab?: 'I' | 'O';
  /** WCO-2677: true when embedded (Output Detail tab) — disables the address-bar sync effect. */
  embedded?: boolean;
}) => {
  const { t } = useLanguage();

  // 1. Primary fetch — the record the user navigated to.
  const { data: primary, isLoading, error } = useQuery(
    ['production-record', productionNumber],
    () => productionRecordService.getRecordDetails(productionNumber),
    { retry: false },
  );

  // 2. Look up linked records via the list endpoint. Searching for the production number
  //    returns rows whose production_number contains it (e.g. searching "900171" finds
  //    900171 itself plus 900172 if 900172.reference_numbers includes 900171).
  const { data: searchResults } = useQuery(
    ['production-record-search', productionNumber],
    () => productionRecordService.getRecords({ search: productionNumber, limit: 20 }),
    { retry: false, onError: () => {} },
  );

  // The INPUT anchor for this production day. If we loaded the input, it's ourself; if we loaded
  // an OUTPUT, it's the input our reference_numbers point at. Every output of the day references
  // this same input, so searching it is what surfaces the sibling-destination records. WCO-2418.
  const inputAnchor = useMemo(() => {
    const records = searchResults?.records || [];
    const self = records.find(r => r.production_number === productionNumber);
    // We're an output → follow our reference to the input.
    const ref = (self?.reference_numbers || []).find(x => x && x !== productionNumber);
    if (ref) return ref;
    // We're the input (nothing referenced, or others reference us) → anchor is ourself.
    return productionNumber;
  }, [searchResults, productionNumber]);

  // 2b. Second-hop search on the input anchor. When we loaded an output, the first search
  //     (keyed on the output number) only returns the output itself — nothing references an
  //     output — so sibling outputs are invisible. Searching the INPUT returns the input plus
  //     EVERY output that references it, so any of the day's numbers resolves the full tab set.
  //     Skipped (same query key, cache hit) when the anchor already equals the loaded number.
  const { data: anchorResults } = useQuery(
    ['production-record-search', inputAnchor],
    () => productionRecordService.getRecords({ search: inputAnchor, limit: 50 }),
    { retry: false, enabled: !!inputAnchor, onError: () => {} },
  );

  // Pick out linked production numbers from BOTH search responses:
  //   - any record whose reference_numbers contains our production number  (we're the input)
  //   - any production number listed in our row's own reference_numbers     (we're the output)
  //   - the input anchor itself, plus every output referencing that anchor  (sibling destinations)
  // Plus the suffix-swap fallback in case the list search misses a row.
  const linkedNumbers = useMemo(() => {
    const out = new Set<string>();
    const records = [...(searchResults?.records || []), ...(anchorResults?.records || [])];

    for (const r of records) {
      if (r.production_number === productionNumber) {
        // Our own row — record what we point at (output → input direction).
        for (const ref of r.reference_numbers || []) {
          if (ref && ref !== productionNumber) out.add(ref);
        }
        continue;
      }
      // Other rows — keep the ones referencing us OR referencing our input anchor
      // (input → output direction, incl. sibling destinations of the same day).
      const refs = r.reference_numbers || [];
      if (refs.includes(productionNumber) || refs.includes(inputAnchor)) {
        out.add(r.production_number);
      }
    }

    // The input anchor itself (when we loaded an output, this pulls in the input tab).
    if (inputAnchor && inputAnchor !== productionNumber) out.add(inputAnchor);

    // Suffix swap fallback (only useful when the list search found nothing for this case).
    const match = productionNumber.match(/^(.+)-([IO])$/);
    if (match) {
      out.add(`${match[1]}-${match[2] === 'I' ? 'O' : 'I'}`);
    }

    return Array.from(out);
  }, [searchResults, anchorResults, productionNumber, inputAnchor]);

  // Fetch details for EVERY linked record (no fixed cap — a multi-output day fans out to N
  // outputs, one per destination). WCO-2416/2418.
  const linkedQueries = useQueries(
    linkedNumbers.map((num) => ({
      queryKey: ['production-record', num],
      queryFn: () => productionRecordService.getRecordDetails(num),
      retry: false,
      onError: () => {},
    })),
  );

  // Keep each linked record DISTINCT (not flattened) so the view can render one Output tab per
  // destination. Merge into a single `sibling` (flattened details) for backward compatibility with
  // the existing Input/Output split, AND expose the per-record list for per-destination tabs.
  const linkedRecords = useMemo(
    () => linkedQueries
      .map((q) => q.data)
      .filter((x): x is RecordDetailsResponse => !!x),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(linkedQueries.map((q) => q.data?.summary?.production_number ?? null))],
  );

  const mergedSibling = useMemo(() => {
    if (linkedRecords.length === 0) return null;
    return {
      summary: linkedRecords[0].summary,
      details: linkedRecords.flatMap((h) => h.details),
    } as RecordDetailsResponse;
  }, [linkedRecords]);

  if (isLoading) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
        {t('common.loading')}
      </div>
    );
  }

  if (error || !primary) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
        {t('productionRecords.notFound')}
      </div>
    );
  }

  return <ProductionRecordDetailView primary={primary} sibling={mergedSibling} linkedRecords={linkedRecords} onBack={onBack} initialTab={initialTab} embedded={embedded} />;
};

export default ProductionRecordDetailView;
